import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Surface, Stat, Badge, PageHeader } from "@/components/ui-kit";
import { Truck, Wrench, Package, CalendarCheck, ArrowUpRight, Warning, GasPump, TrendUp, TrendDown } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [trend, setTrend] = useState([]);
    const [pnl, setPnl] = useState(null);

    // Month picker — default to current month
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/reports/maintenance-trend").then((r) => setTrend(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        api.get(`/dashboard/mileage-pnl?month=${month}`).then((r) => setPnl(r.data)).catch(() => setPnl(null));
    }, [month]);

    const fmtINR = (n) => {
        if (n == null || isNaN(n)) return "—";
        const sign = n > 0 ? "" : n < 0 ? "−" : "";
        return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    };

    const pnlDeltaVsPrev = useMemo(() => {
        if (!pnl || !pnl.previous_month) return null;
        const cur = pnl.net_pnl_inr;
        const prev = pnl.previous_month.net_pnl_inr;
        if (cur == null || prev == null) return null;
        return cur - prev;
    }, [pnl]);

    if (!stats) {
        return <div className="p-8 label-mono">Loading dashboard…</div>;
    }

    return (
        <div className="p-6 lg:p-8 animate-fade-in" data-testid="dashboard-page">
            <PageHeader
                eyebrow="OPERATIONS · LIVE"
                title="Fleet Control Room"
                subtitle="Real-time view of vehicles, maintenance, inventory and consumables."
            />

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat label="VEHICLES ACTIVE" value={stats.vehicles_active} hint={`of ${stats.vehicles_total} total`} accent="healthy" testid="stat-vehicles" />
                <Stat label="WO IN PROGRESS" value={stats.wo_in_progress} hint={`${stats.wo_open} open · ${stats.wo_done} done`} accent="info" testid="stat-wo" />
                <Stat label="LOW STOCK" value={stats.low_stock_count} hint="parts at/below min threshold" accent={stats.low_stock_count > 0 ? "critical" : "default"} testid="stat-stock" />
                <Stat label="PM DUE" value={stats.pm_due_count} hint="schedules approaching" accent={stats.pm_due_count > 0 ? "warning" : "default"} testid="stat-pm" />
            </div>

            {/* Fuel P&L summary */}
            <Surface className="mb-6 overflow-hidden" data-testid="dashboard-pnl-card">
                <div className="px-5 py-4 border-b border-fleet-border flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <GasPump size={18} className="text-fleet-inkSoft" />
                        <div>
                            <div className="label-mono">FLEET FUEL P&amp;L</div>
                            <h3 className="font-display text-lg font-semibold mt-0.5">Monthly summary</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-fleet-border rounded-md font-mono focus:outline-none focus:border-fleet-ink"
                            data-testid="pnl-month-picker"
                        />
                        <Link to={`/mileage`} className="text-sm text-fleet-inkSoft hover:text-fleet-ink flex items-center gap-1">
                            Open Mileage <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>

                {!pnl ? (
                    <div className="px-5 py-8 text-sm text-fleet-inkSoft text-center">Loading…</div>
                ) : pnl.log_count === 0 ? (
                    <div className="px-5 py-8 text-sm text-fleet-inkSoft text-center" data-testid="pnl-empty">
                        No mileage logs with rate/litre captured for {month}. Add Rate / Litre on a refuel log in the
                        <Link to="/mileage" className="text-fleet-accent hover:underline mx-1">Mileage</Link>
                        module to start tracking fleet fuel P&amp;L.
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-b border-fleet-border">
                            <div className="px-5 py-4 border-r border-fleet-border">
                                <div className="label-mono">NET P&amp;L</div>
                                <div className={`font-mono text-2xl font-semibold mt-1 ${pnl.net_pnl_inr > 0 ? "text-rose-700" : pnl.net_pnl_inr < 0 ? "text-emerald-700" : "text-fleet-ink"}`}
                                    data-testid="pnl-net">
                                    {pnl.net_pnl_inr > 0 ? "−" : pnl.net_pnl_inr < 0 ? "+" : ""}
                                    ₹{Math.abs(pnl.net_pnl_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-fleet-inkSoft mt-0.5">
                                    {pnl.net_pnl_inr > 0 ? "Net loss" : pnl.net_pnl_inr < 0 ? "Net gain" : "Break-even"}
                                    {pnlDeltaVsPrev != null && pnlDeltaVsPrev !== 0 && (
                                        <span className={`ml-2 inline-flex items-center gap-0.5 ${pnlDeltaVsPrev > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                            {pnlDeltaVsPrev > 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}
                                            {fmtINR(Math.abs(pnlDeltaVsPrev))} vs prev
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="px-5 py-4 border-r border-fleet-border">
                                <div className="label-mono">LOSS (₹)</div>
                                <div className="font-mono text-xl font-semibold mt-1 text-rose-700" data-testid="pnl-loss">
                                    −₹{pnl.total_loss_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-fleet-inkSoft mt-0.5">Extra fuel consumed at recorded rates</div>
                            </div>
                            <div className="px-5 py-4 border-r border-fleet-border">
                                <div className="label-mono">GAIN (₹)</div>
                                <div className="font-mono text-xl font-semibold mt-1 text-emerald-700" data-testid="pnl-gain">
                                    +₹{pnl.total_gain_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-fleet-inkSoft mt-0.5">Fuel saved at recorded rates</div>
                            </div>
                            <div className="px-5 py-4">
                                <div className="label-mono">SPEND · VOLUME</div>
                                <div className="font-mono text-xl font-semibold mt-1">
                                    ₹{pnl.total_fuel_cost_inr.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                </div>
                                <div className="text-xs text-fleet-inkSoft mt-0.5">
                                    {pnl.total_fuel_litres.toLocaleString("en-IN")} L · {pnl.total_distance_km.toLocaleString()} km · {pnl.log_count} refuel(s)
                                </div>
                            </div>
                        </div>

                        {/* Worst & best performers drill-down */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                            <div className="px-5 py-4 border-r border-fleet-border">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="label-mono text-rose-700">WORST PERFORMERS</div>
                                    <span className="text-xs text-fleet-inkSoft">Click reg # to drill-down</span>
                                </div>
                                {pnl.top_loss_vehicles.length === 0 ? (
                                    <div className="text-xs text-fleet-inkSoft py-2">No vehicles with monetary loss this month.</div>
                                ) : (
                                    <ul className="space-y-1.5">
                                        {pnl.top_loss_vehicles.map((v) => (
                                            <li key={v.vehicle_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-fleet-border last:border-0" data-testid={`pnl-loss-${v.vehicle_id}`}>
                                                <Link to={`/mileage?vehicle=${v.vehicle_id}&month=${month}`}
                                                    className="font-mono text-sm font-medium hover:underline">
                                                    {v.reg_number || v.vehicle_id.slice(0, 8)}
                                                </Link>
                                                <span className="text-xs text-fleet-inkSoft truncate flex-1 ml-2">
                                                    {[v.make, v.model].filter(Boolean).join(" ")} · {v.log_count} refuel(s)
                                                </span>
                                                <span className="font-mono text-sm text-rose-700 font-semibold">−₹{v.amount_delta_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="px-5 py-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="label-mono text-emerald-700">BEST PERFORMERS</div>
                                    <span className="text-xs text-fleet-inkSoft">Beat the fixed mileage</span>
                                </div>
                                {pnl.top_gain_vehicles.length === 0 ? (
                                    <div className="text-xs text-fleet-inkSoft py-2">No vehicles with monetary gain this month.</div>
                                ) : (
                                    <ul className="space-y-1.5">
                                        {pnl.top_gain_vehicles.map((v) => (
                                            <li key={v.vehicle_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-fleet-border last:border-0" data-testid={`pnl-gain-${v.vehicle_id}`}>
                                                <Link to={`/mileage?vehicle=${v.vehicle_id}&month=${month}`}
                                                    className="font-mono text-sm font-medium hover:underline">
                                                    {v.reg_number || v.vehicle_id.slice(0, 8)}
                                                </Link>
                                                <span className="text-xs text-fleet-inkSoft truncate flex-1 ml-2">
                                                    {[v.make, v.model].filter(Boolean).join(" ")} · {v.log_count} refuel(s)
                                                </span>
                                                <span className="font-mono text-sm text-emerald-700 font-semibold">+₹{Math.abs(v.amount_delta_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Surface>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Maintenance trend chart */}
                <Surface className="p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="label-mono">MAINTENANCE SPEND</div>
                            <h3 className="font-display text-lg font-semibold mt-0.5">Last 6 months</h3>
                        </div>
                        <div className="font-mono tabular text-sm text-fleet-inkSoft">
                            ₹{Number(stats.total_spend_30d || 0).toLocaleString("en-IN")} · 30d
                        </div>
                    </div>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trend}>
                                <CartesianGrid stroke="#E2E8F0" strokeDasharray="0" vertical={false} />
                                <XAxis dataKey="month" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Line type="monotone" dataKey="preventive" stroke="#2563EB" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="breakdown" stroke="#E11D48" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="inspection" stroke="#10B981" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-fleet-inkSoft">
                        <span><span className="inline-block w-3 h-0.5 bg-blue-600 align-middle mr-1.5" />Preventive</span>
                        <span><span className="inline-block w-3 h-0.5 bg-rose-600 align-middle mr-1.5" />Breakdown</span>
                        <span><span className="inline-block w-3 h-0.5 bg-emerald-500 align-middle mr-1.5" />Inspection</span>
                    </div>
                </Surface>

                {/* Asset summary */}
                <Surface className="p-5">
                    <div className="label-mono">ASSETS</div>
                    <h3 className="font-display text-lg font-semibold mt-0.5 mb-4">In service</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between py-2 border-b border-fleet-border">
                            <div className="flex items-center gap-2 text-sm"><Truck size={16} /> Vehicles</div>
                            <div className="font-mono tabular text-sm">{stats.vehicles_active}</div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-fleet-border">
                            <div className="flex items-center gap-2 text-sm"><Wrench size={16} /> Tyres fitted</div>
                            <div className="font-mono tabular text-sm">{stats.tyres_active}</div>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2 text-sm"><Package size={16} /> Batteries fitted</div>
                            <div className="font-mono tabular text-sm">{stats.batteries_active}</div>
                        </div>
                    </div>
                </Surface>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Recent WO */}
                <Surface className="lg:col-span-2">
                    <div className="px-5 py-4 border-b border-fleet-border flex items-center justify-between">
                        <div>
                            <div className="label-mono">WORK ORDERS</div>
                            <h3 className="font-display text-lg font-semibold mt-0.5">Recent</h3>
                        </div>
                        <Link to="/work-orders" className="text-sm text-fleet-inkSoft hover:text-fleet-ink flex items-center gap-1">
                            View board <ArrowUpRight size={14} />
                        </Link>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="label-mono px-5 py-2.5 font-normal">WO #</th>
                                <th className="label-mono px-5 py-2.5 font-normal">Title</th>
                                <th className="label-mono px-5 py-2.5 font-normal">Type</th>
                                <th className="label-mono px-5 py-2.5 font-normal">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(stats.recent_work_orders || []).map((w) => (
                                <tr key={w.id} className="border-t border-fleet-border data-grid-row" data-testid={`recent-wo-${w.id}`}>
                                    <td className="px-5 py-2.5 font-mono">{w.wo_number}</td>
                                    <td className="px-5 py-2.5">{w.title}</td>
                                    <td className="px-5 py-2.5"><Badge variant={w.type}>{w.type}</Badge></td>
                                    <td className="px-5 py-2.5"><Badge variant={w.status}>{w.status?.replace("_", " ")}</Badge></td>
                                </tr>
                            ))}
                            {(stats.recent_work_orders || []).length === 0 && (
                                <tr><td colSpan={4} className="px-5 py-10 text-center text-fleet-inkSoft text-sm">No work orders yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Surface>

                {/* Alerts */}
                <Surface>
                    <div className="px-5 py-4 border-b border-fleet-border">
                        <div className="label-mono">ALERTS</div>
                        <h3 className="font-display text-lg font-semibold mt-0.5">Needs attention</h3>
                    </div>
                    <div className="divide-y divide-fleet-border">
                        {(stats.low_stock_items || []).map((i) => (
                            <div key={i.id} className="px-5 py-3 flex items-center gap-3">
                                <div className="h-8 w-8 rounded-md border border-rose-200 bg-rose-50 flex items-center justify-center text-fleet-critical">
                                    <Warning size={14} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">{i.name}</div>
                                    <div className="text-xs text-fleet-inkSoft">SKU {i.sku} · qty <span className="font-mono">{i.quantity}</span> / min <span className="font-mono">{i.min_stock}</span></div>
                                </div>
                                <Badge variant="critical">LOW</Badge>
                            </div>
                        ))}
                        {(stats.pm_due || []).map((p) => (
                            <div key={p.id} className="px-5 py-3 flex items-center gap-3">
                                <div className="h-8 w-8 rounded-md border border-amber-200 bg-amber-50 flex items-center justify-center text-amber-700">
                                    <CalendarCheck size={14} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">{p.name}</div>
                                    <div className="text-xs text-fleet-inkSoft">{p.vehicle_reg} · {p.km_since_last} km since last</div>
                                </div>
                                <Badge variant="warning">DUE</Badge>
                            </div>
                        ))}
                        {(stats.low_stock_items?.length || 0) + (stats.pm_due?.length || 0) === 0 && (
                            <div className="px-5 py-10 text-center text-sm text-fleet-inkSoft">All systems healthy.</div>
                        )}
                    </div>
                </Surface>
            </div>
        </div>
    );
}
